
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { PawPrint, MessageSquare, Share, Camera, AlertTriangle, Sparkles, Home as HomeIcon, Phone, MapPin, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import LikeButton from './LikeButton';
import PostCardActions from './PostCardActions';
import ShareModal from './ShareModal';
import CommentsModal from './CommentsModal';
import FollowButton from './FollowButton';
import MediaCarousel from './MediaCarousel';
import { MatchButton } from './MatchButton';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PostCardProps {
  post: {
    id: string;
    caption: string;
    created_at: string;
    media: { url: string; media_type?: string }[];
    pet: {
      id: string;
      name: string;
      photo_url?: string;
      user_id?: string;
      species?: string;
      is_memorial?: boolean;
    };
    likes_count: number;
    comments_count: number;
    user_has_liked: boolean;
    user_id?: string;
    post_type?: string;
    privacy_setting?: 'public' | 'private' | 'followers_only';
    contact_phone?: string | null;
    lost_lat?: number | null;
    lost_lng?: number | null;
    lost_address?: string | null;
    lost_last_seen_at?: string | null;
    lost_resolved_at?: string | null;
    lost_bump_count?: number | null;
  };
}

const PostCard = ({ post }: PostCardProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [isFoundDialogOpen, setIsFoundDialogOpen] = useState(false);
  const [isMarkingFound, setIsMarkingFound] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();


  const isMemorial = post.pet.is_memorial === true;
  const isLost = post.post_type === 'lost_pet';
  const isMilestone = post.post_type === 'milestone';
  const isAdoption = post.post_type === 'adoption_post';
  const isResolved = isLost && !!post.lost_resolved_at;
  const ownerId = post.user_id || post.pet.user_id;
  const isOwner = !!user?.id && !!ownerId && user.id === ownerId;

  // Themed wrapper per post_type
  const wrapperTone = isResolved
    ? 'ring-2 ring-emerald-500/60 bg-emerald-50/40'
    : isLost
      ? 'lost-alert-card'
      : isMilestone
        ? 'ring-2 ring-amber-400/70 bg-amber-50/40'
        : isAdoption
          ? 'ring-2 ring-teal-500/70 bg-teal-50/40'
          : '';

  const handleMarkAsFound = async () => {
    if (!isOwner || isMarkingFound) return;
    setIsMarkingFound(true);
    try {
      const { error } = await supabase
        .from('memory_posts')
        .update({ lost_resolved_at: new Date().toISOString() })
        .eq('id', post.id);
      if (error) throw error;
      toast.success(
        t('postTypes.lostPet.markFoundSuccess', '{{name}} has been marked as found 🎉', { name: post.pet.name })
      );
      setIsFoundDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['feed-posts'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
      queryClient.invalidateQueries({ queryKey: ['pet-posts'] });
    } catch (e) {
      console.error('mark as found failed', e);
      toast.error(t('postTypes.lostPet.markFoundError', 'Could not mark as found. Please try again.'));
    } finally {
      setIsMarkingFound(false);
    }
  };

  const renderBanner = () => {
    if (isLost) {
      if (isResolved) {
        return (
          <div className="found-alert-banner flex items-center gap-2 px-4 py-3">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span className="text-sm font-bold uppercase tracking-wide">
              {t('postTypes.lostPet.resolvedBadge', 'Pet Found ✓')}
            </span>
          </div>
        );
      }
      return (
        <div className="lost-alert-banner flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <AlertTriangle className="h-6 w-6 shrink-0" />
            <span className="text-base font-bold uppercase break-words">
              {t('postTypes.lostPet.banner', 'Lost Pet Alert')}
            </span>
          </div>
          {isOwner && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setIsFoundDialogOpen(true)}
              className="rounded-full text-xs font-semibold shrink-0"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {t('postTypes.lostPet.markFound', 'Mark as Found')}
            </Button>
          )}
        </div>
      );
    }

    if (isMilestone) {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
          <Sparkles className="h-4 w-4 shrink-0" />
          <span className="text-sm font-bold uppercase tracking-wide">
            {t('postTypes.milestone.banner', 'Milestone')}
          </span>
        </div>
      );
    }
    if (isAdoption) {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
          <HomeIcon className="h-4 w-4 shrink-0" />
          <span className="text-sm font-bold uppercase tracking-wide">
            {t('postTypes.adoption.banner', 'Available for Adoption')}
          </span>
        </div>
      );
    }
    if (post.post_type === 'profile_photo') {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700">
          <Camera className="h-4 w-4" />
          <span className="text-xs font-medium">{t('postTypes.profilePhoto.badge')}</span>
        </div>
      );
    }
    return null;
  };

  const renderLostActions = () => {
    if (!isLost || isResolved) return null;

    const phone = (post.contact_phone || '').trim();
    const hasCoords = post.lost_lat != null && post.lost_lng != null;
    const waNumber = phone.replace(/[^\d+]/g, '').replace(/^\+/, '');
    const mapUrl = hasCoords
      ? `https://www.google.com/maps/search/?api=1&query=${post.lost_lat},${post.lost_lng}`
      : null;

    return (
       <div className="lost-alert-details px-4 py-3 space-y-2">
        {post.lost_address && (
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{post.lost_address}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {phone && (
            <>
              <Button asChild variant="destructive" size="sm" className="rounded-full text-xs">
              <a
                href={`tel:${phone}`}
              >
                <Phone className="h-3.5 w-3.5" />
                {t('postTypes.lostPet.call', 'Call')}
              </a>
              </Button>
              {waNumber && (
                <Button asChild variant="outline" size="sm" className="rounded-full text-xs">
                <a
                  href={`https://wa.me/${waNumber}?text=${encodeURIComponent(
                    t('postTypes.lostPet.waPrefill', 'I have information about your lost pet')
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Phone className="h-3.5 w-3.5" />
                  WhatsApp
                </a>
                </Button>
              )}
            </>
          )}
          {mapUrl && (
            <Button asChild variant="outline" size="sm" className="rounded-full text-xs">
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MapPin className="h-3.5 w-3.5" />
              {t('postTypes.lostPet.viewMap', 'View on map')}
            </a>
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`mb-8 md:rounded-lg md:overflow-hidden md:shadow-md ${isMemorial ? 'memorial-celebration-border' : ''} ${wrapperTone}`}>
      {renderBanner()}

      {/* Post Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <Link to={`/pet/${post.pet.id}`} className="flex items-center">
          <div className={`mr-2 ${isMemorial ? 'memorial-avatar-halo rounded-full' : ''}`}>
            <Avatar className="h-8 w-8">
              {post.pet.photo_url ? (
                <AvatarImage src={post.pet.photo_url} alt={post.pet.name} />
              ) : (
                <AvatarFallback className="bg-pet-purple/20 text-pet-purple">
                  <PawPrint className="h-4 w-4" />
                </AvatarFallback>
              )}
            </Avatar>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium hover:underline">{post.pet.name}</span>
              {isMemorial && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs italic text-white bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 whitespace-nowrap">
                  {t('common.foreverLoved')}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
            </span>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          {!isLost && (post.user_id || post.pet.user_id) && (
            <MatchButton
              petId={post.pet.id}
              petName={post.pet.name}
              petSpecies={post.pet.species}
              ownerId={post.user_id || post.pet.user_id}
              isMemorial={post.pet.is_memorial}
            />
          )}
          {ownerId && (
            <FollowButton targetUserId={ownerId} />
          )}
        </div>
      </div>

      {/* Post Media */}
      {post.media.length > 0 && (
        <Link to={`/post/${post.id}`} className="block">
          <div className="relative w-full max-h-[75vh] overflow-hidden bg-gray-100 flex items-center justify-center">
            <MediaCarousel
              media={post.media}
              alt={post.caption || 'Post image'}
              containerClassName="max-h-[75vh]"
            />
          </div>
        </Link>
      )}

      {/* Lost Pet quick actions */}
      {renderLostActions()}

      {/* Post Caption */}
      {post.caption && (
        <div className="p-4 pt-2">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{post.caption}</p>
        </div>
      )}

      {/* Post Actions */}
      <div className="px-4 py-2 flex justify-between">
        <div className="flex items-center space-x-3">
          <LikeButton id={post.id} showModal={true} />
          <button
            type="button"
            onClick={() => setIsCommentsModalOpen(true)}
            className="flex items-center text-gray-500 hover:text-gray-700"
            aria-label={t('comments.openComments')}
          >
            <MessageSquare className="h-5 w-5 mr-1" />
            <span>{post.comments_count || 0}</span>
          </button>
          <button
            onClick={() => setIsShareModalOpen(true)}
            className="flex items-center text-gray-500 hover:text-gray-700"
          >
            <Share className="h-5 w-5 mr-1" />
            <span>{t('common.share', 'Share')}</span>
          </button>
        </div>
        <PostCardActions
          postId={post.id}
          petName={post.pet?.name}
          userId={post.user_id}
          caption={post.caption}
          privacySetting={post.privacy_setting}
        />
      </div>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        postId={post.id}
        petName={post.pet.name}
      />

      <CommentsModal
        isOpen={isCommentsModalOpen}
        onClose={() => setIsCommentsModalOpen(false)}
        postId={post.id}
        petName={post.pet?.name}
      />

      <AlertDialog open={isFoundDialogOpen} onOpenChange={setIsFoundDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('postTypes.lostPet.markFoundTitle', 'Mark {{name}} as found?', { name: post.pet.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'postTypes.lostPet.markFoundConfirm',
                'Automatic re-posts will stop and this alert will be removed from the Community feed. The post will remain on your profile.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMarkingFound}>
              {t('common.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleMarkAsFound(); }}
              disabled={isMarkingFound}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {t('postTypes.lostPet.markFound', 'Mark as Found')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>

  );
};

export default PostCard;
